import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { useTenantData } from "@/hooks/shared/useTenantData";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { TuitionTiersApi } from "@/openapi/generated";
import { parseTuitionRatePlans } from "../utils/parse-tuition-rate-plan";

interface UseTuitionRatePlansGetProps {
  enabled?: boolean;
  onlyActive?: boolean;
  includeTiers?: boolean;
}

export const useTuitionRatePlansGet = ({
  enabled = true,
  onlyActive = true,
  includeTiers = false,
}: UseTuitionRatePlansGetProps = {}) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const { data, isError, isLoading, isSuccess, isPending, refetch } = useQuery({
    queryKey: [QueryKeys.TuitionRatePlans, { onlyActive, includeTiers }],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(TuitionTiersApi, accessToken);
      const response = await service.apiTuitionTiersRatePlansGetRaw({
        includeTiers,
        onlyActive,
        xTenantID: tenantId,
      });
      return parseTuitionRatePlans(await response.raw.json());
    },
    enabled: enabled !== false && !!accessToken,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
  };
};
